/**
 * FAQ_Block 候选条目的装配与渲染门限。
 *
 * 服务端（`GET /api/front/faq?scope=...`）负责「已发布 + 有序 + 有界」：
 * 已按 `sort` 升序、`sort` 相同按 `id` 升序返回，单次上限 50 条。
 * 本模块负责「分组顺序 + 去重 + 展示条数」：页面 scope 条目在前、`SITE` 条目
 * 追加其后，去掉问法重复的条目，最后取前 12 条（需求 6.2、6.11）。
 * 两层职责不重叠，所以这里不做「已发布」过滤，也不重新实现分页。
 *
 * 是否渲染由 `shouldRenderFaqBlock()` 单点判定：FAQ_Block 与 `FAQPage` JSON-LD
 * 共用同一个判定结果，二者要么同时输出、要么同时不输出（需求 6.1、6.9、6.12）。
 * 判定与装配写在同一个模块里，就是为了让「区块渲染了但 JSON-LD 没输出」
 * 这类不一致在源码层面无处产生。
 *
 * 纯函数：不读 DOM、不发网络请求、不读全局状态，运行时（`FaqBlock.tsx`）、
 * 构建期预渲染与巡检器共用这一份实现。
 *
 * 对应需求：6.1、6.2、6.9、6.11、6.12
 */

import type { PageType } from "./types";

/** 「全站」归属范围的取值，与 `biz_faq.scope` 及前台接口参数逐字符一致 */
export const FAQ_SITE_SCOPE = "SITE";

/** FAQ_Entry 的归属范围：某个 Page_Type 或「全站」（需求 5.3、5.14） */
export type FaqScope = PageType | typeof FAQ_SITE_SCOPE;

/**
 * 一条 FAQ_Entry 中本层用到的字段。
 *
 * `status` / `delFlag` 等字段不在这里出现：前台接口只返回已发布未删除条目，
 * 把它们纳入类型会诱使调用方在前端再过滤一遍，形成两处口径。
 */
export type FaqEntry = {
  /** 主键，排序值相同时的次级排序键（需求 6.11） */
  id: number;
  /** 问题文本，去首尾空白后 5-60 字符（由服务端校验保证，需求 5.3） */
  question: string;
  /** 答案文本，去首尾空白后 40-500 字符（由服务端校验保证，需求 5.3） */
  answer: string;
  /** 归属范围 */
  scope: FaqScope;
  /** 排序值 0-9999，升序（需求 5.7） */
  sort: number;
};

/** FAQ_Block 的问答条目上限（需求 6.2） */
export const FAQ_BLOCK_MAX_ENTRIES = 12;

/** 渲染 FAQ_Block 与 `FAQPage` JSON-LD 所需的最少条目数（需求 6.1、6.9） */
export const FAQ_BLOCK_MIN_ENTRIES = 5;

/**
 * 排序值兜底：非有限数（`null`、`undefined`、`NaN`、字符串等脏数据）排到组尾。
 *
 * 不直接把脏值交给比较函数：`NaN` 参与比较时两个方向都返回 `false`，
 * 比较函数会退化成「返回 0」，得到的顺序依赖输入排列而非排序键，
 * 对同一份数据的两次构建可能产出不同 Raw_HTML。
 */
function toSortKey(value: number): number {
  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
}

/** 问法的比对口径：去首尾空白后逐字符比较（需求 6.11） */
function toQuestionKey(entry: FaqEntry): string {
  return typeof entry?.question === "string" ? entry.question.trim() : "";
}

/** `sort` 升序、`sort` 相同按 `id` 升序（需求 6.11） */
function compareBySortThenId(left: FaqEntry, right: FaqEntry): number {
  const sortDelta = toSortKey(left.sort) - toSortKey(right.sort);
  if (sortDelta !== 0) {
    return sortDelta;
  }
  return toSortKey(left.id) - toSortKey(right.id);
}

/** 只保留可用条目，并复制一份数组：排序不得改动调用方传入的数组 */
function sortedGroup(entries: readonly FaqEntry[] | null | undefined): FaqEntry[] {
  if (!Array.isArray(entries)) {
    return [];
  }
  return entries.filter((entry): entry is FaqEntry => Boolean(entry)).sort(compareBySortThenId);
}

/**
 * 组装某个页面的 FAQ_Block 条目。
 *
 * 顺序：页面 scope 条目（按 `sort` 升序、`id` 升序）在前，`SITE` 条目按同规则
 * 追加其后；随后移除问题文本去首尾空白后与**前序条目**重复的条目——「前序」
 * 意味着页面级问法胜出，同一个问法被全站条目重复提问时保留页面级答案；
 * 最后取前 12 条（需求 6.2、6.11）。
 *
 * 传入的两个数组都不会被修改；条目对象按引用原样返回，因此区块与
 * `FAQPage` JSON-LD 读到的问答文本必然同源（需求 6.4）。
 *
 * @param pageScopeEntries 归属范围等于该页 Page_Type 的已发布条目
 * @param siteEntries 归属范围为「全站」的已发布条目
 * @returns 至多 12 条、问法两两不同的条目，顺序即区块内的出现顺序
 *
 * 对应需求：6.2、6.11
 */
export function assembleFaqBlock(
  pageScopeEntries: readonly FaqEntry[] | null | undefined,
  siteEntries: readonly FaqEntry[] | null | undefined,
): FaqEntry[] {
  const ordered = [...sortedGroup(pageScopeEntries), ...sortedGroup(siteEntries)];

  const seenQuestions = new Set<string>();
  const assembled: FaqEntry[] = [];

  for (const entry of ordered) {
    if (assembled.length >= FAQ_BLOCK_MAX_ENTRIES) {
      break;
    }
    const questionKey = toQuestionKey(entry);
    if (seenQuestions.has(questionKey)) {
      continue;
    }
    seenQuestions.add(questionKey);
    assembled.push(entry);
  }

  return assembled;
}

/**
 * 是否渲染 FAQ_Block 与 `FAQPage` JSON-LD。
 *
 * 判定口径为「问法去重后的条目数不少于 5」。按去重后计数而不是按 `list.length`
 * 计数，是为了让本函数对「装配结果」与「未装配的候选池」给出同一个答案：
 * 去重后的条目数与分组顺序无关，而装配结果的长度恒等于
 * `min(12, 去重后条目数)`，因此
 * `shouldRenderFaqBlock(pool) === (assembleFaqBlock(...).length >= 5)` 恒成立。
 * 调用方少写一次装配也不会得到与实际渲染不一致的判定。
 *
 * 区块与 JSON-LD 共用这一个返回值：为 `false` 时两者同时省略，页面其余区块、
 * Page_Metadata 与其余 JSON-LD 不受影响，并由调用方记录 URL、归属范围与
 * 实际条目数的日志（需求 6.9）。Page_Type 是否属于 FAQ 承载页由
 * `PAGE_TYPE_META_PRESET[pageType].renderFaq` 决定（需求 6.1、6.12），
 * 与条目数门限是两个独立条件，调用方需同时满足。
 *
 * @param list FAQ 条目列表，可以是 `assembleFaqBlock()` 的结果或原始候选池
 * @returns 条目数（去重后）不少于 5 时为 `true`
 *
 * 对应需求：6.1、6.9、6.12
 */
export function shouldRenderFaqBlock(list: readonly FaqEntry[] | null | undefined): boolean {
  if (!Array.isArray(list)) {
    return false;
  }

  const seenQuestions = new Set<string>();
  for (const entry of list) {
    if (!entry) {
      continue;
    }
    seenQuestions.add(toQuestionKey(entry));
    if (seenQuestions.size >= FAQ_BLOCK_MIN_ENTRIES) {
      return true;
    }
  }

  return false;
}
